import { createContext, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createAudioPlayer, type AudioPlayer, type AudioPlayerState, type AudioSource } from "@yetric/audioplayer";
import { when } from "mobx";
import { Button } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { authStore, listeningStore, playerStore, playlistStore, sleepTimerStore } from "@/context/stores";
import { AutoAdvance } from "@/core/autoAdvance";
import { type PodcastAudioSource, toAudioSource } from "@/core/audioSource";
import { trackEpisodeFinished } from "@/core/listenEvents";
import { attachMediaSession } from "@/core/mediaSession";
import { rateForPodcast, readPlayerClosedAt, readPlayerPrefs, updatePlayerPrefs } from "@/core/playerPrefs";
import { restoreLastEpisode } from "@/core/restorePlayer";

export interface AudioState {
    audio: AudioSource | undefined;
    isPlaying: boolean;
    setAudio: (source: PodcastAudioSource, startAt?: number) => void;
    play: () => void;
    pause: () => void;
    seek: (time?: number) => number;
    duration: () => number;
    rate: (speed?: number) => number;
    unload: () => void;
    /** Calls `listener` whenever the playback state changes; returns the way to stop. Stable between renders. */
    subscribe: (listener: () => void) => () => void;
}

export const AudioContext = createContext<AudioState>({} as AudioState);

const viewOf = ({ currentSource, rate, status }: AudioPlayerState) => ({
    currentSource,
    rate,
    status,
});

const sameView = (view: ReturnType<typeof viewOf>, state: AudioPlayerState) =>
    view.currentSource === state.currentSource && view.rate === state.rate && view.status === state.status;

const PROGRESS_SAVE_INTERVAL_MS = 5000;

export const AudioProvider = ({ children }: { children: ReactNode }) => {
    const [player] = useState<AudioPlayer>(() => createAudioPlayer());
    // Only what the context exposes: the player reports several times a second, and this must not re-render for those
    const [playerState, setPlayerState] = useState(() => viewOf(player.getState()));
    // True from loading an episode until its saved position has been applied, so the
    // position 0 reported meanwhile does not overwrite the one we are resuming from
    const resuming = useRef(false);
    // Speed belongs to the device and to the show: what is chosen is remembered for the podcast playing.
    // The device settings are read where they are used, so a change made on the settings page applies here too.
    const ratePodcast = useRef<number | null>(null);

    const loadSource = useCallback(
        (source: PodcastAudioSource, startAt?: number) => {
            resuming.current = !!startAt && startAt > 0;
            ratePodcast.current = null;
            player.setRate(rateForPodcast(readPlayerPrefs(), source.podcastId));
            ratePodcast.current = source.podcastId;
            void player
                .load(source)
                .then(() => {
                    if (startAt && startAt > 0) {
                        player.seek(startAt);
                    }
                })
                .catch((error) => {
                    console.error("Failed to load audio source", error);
                })
                .finally(() => {
                    resuming.current = false;
                });
        },
        [player]
    );

    useEffect(() => {
        // Remember where the listener is in the episode that is loaded
        let lastSaved = 0;
        const saveProgress = (state: AudioPlayerState, force = false) => {
            const { currentEpisode, currentPodcast } = playerStore;
            if (resuming.current || !playerStore.active || state.currentSourceId !== String(currentEpisode.id)) {
                return;
            }
            const now = Date.now();
            if (!force && now - lastSaved < PROGRESS_SAVE_INTERVAL_MS) {
                return;
            }
            lastSaved = now;
            listeningStore.record(currentEpisode, currentPodcast, state.currentTime, state.duration);
        };

        const mediaSession = attachMediaSession(player);
        // Sleep timer: counts playing time, fades the volume out at the end, then pauses
        let fadedFrom: number | null = null;

        // Speed and volume belong to the device, not the account, so they are remembered here
        // (a show's speed is applied when its episode loads)
        const initial = readPlayerPrefs();
        if (initial.rate !== undefined) player.setRate(initial.rate);
        if (initial.volume !== undefined) player.setVolume(initial.volume);
        if (initial.muted !== undefined) player.setMuted(initial.muted);

        const unsubscribe = player.subscribe((nextState) => {
            setPlayerState((current) => (sameView(current, nextState) ? current : viewOf(nextState)));
            playerStore.setPlayingState(nextState.status === "playing");

            // The sleep timer fades the volume; that is not a choice to remember
            if (fadedFrom === null) {
                const { rate, volume, muted } = nextState;
                const current = readPlayerPrefs();
                const podcastId = ratePodcast.current;
                const rateChanged = podcastId !== null && rate !== rateForPodcast(current, podcastId);
                if (rateChanged || volume !== current.volume || muted !== current.muted) {
                    const rates = rateChanged ? { ...current.rates, [String(podcastId)]: rate } : current.rates;
                    updatePlayerPrefs({ rates, volume, muted });
                }
            }
        });
        let lastTick = Date.now();
        const sleepInterval = setInterval(() => {
            const now = Date.now();
            const elapsed = now - lastTick;
            lastTick = now;
            if (!sleepTimerStore.isActive && fadedFrom === null) {
                return;
            }

            const state = player.getState();
            const { volumeFactor, stop } = sleepTimerStore.tick(
                elapsed,
                state.status === "playing",
                state.currentSourceId
            );
            if (stop) {
                player.pause();
                player.setVolume(fadedFrom ?? state.volume);
                fadedFrom = null;
            } else if (volumeFactor < 1) {
                fadedFrom ??= state.volume;
                player.setVolume(fadedFrom * volumeFactor);
            } else if (fadedFrom !== null) {
                player.setVolume(fadedFrom);
                fadedFrom = null;
            }
        }, 1000);

        const unsubscribeTime = player.on("timeupdate", ({ state }) => saveProgress(state));
        // Plays on through the saved list when an episode ends
        const autoAdvance = new AutoAdvance({
            queue: () => playlistStore.episodes,
            isPlayed: (id) => listeningStore.isPlayed(id),
            removeFromQueue: (episode) => void playlistStore.remove(episode),
            enabled: () => readPlayerPrefs().autoAdvance !== false,
            stillIdleOn: (id) => {
                const { currentSourceId, status } = player.getState();
                return currentSourceId === String(id) && status !== "playing";
            },
            start: ({ episode, podcast }) => {
                loadSource(toAudioSource(episode, podcast), listeningStore.resumePosition(episode.id));
                playerStore.setCurrentEpisode(episode, podcast);
                playerStore.play(0);
                void player.play().catch((error) => {
                    console.error("Failed to start the next episode", error);
                    playerStore.setPlayingState(false);
                });
            },
            announce: ({ episode }, cancel) => {
                const id = "auto-advance";
                notifications.hide(id);
                notifications.show({
                    id,
                    title: "Spelar nästa",
                    message: (
                        <>
                            {episode.title}{" "}
                            <Button size="compact-xs" variant="default" onClick={cancel}>
                                Avbryt
                            </Button>
                        </>
                    ),
                    autoClose: false,
                    withCloseButton: false,
                });
                return () => notifications.hide(id);
            },
        });
        const unsubscribeEnded = player.on("ended", ({ state }) => {
            const endedId = state.currentSourceId;
            // Read before episodeEnded() clears it: a timer set to end with this episode wants silence
            const mode = sleepTimerStore.mode;
            const stopAfterEpisode = !!endedId && mode?.kind === "episode-end" && mode.episodeId === endedId;
            if (endedId) {
                sleepTimerStore.episodeEnded(endedId);
                trackEpisodeFinished(authStore.isSignedIn);
            }
            const { currentEpisode, currentPodcast } = playerStore;
            if (endedId === String(currentEpisode.id)) {
                listeningStore.markPlayed(currentEpisode, currentPodcast);
            }
            if (endedId) autoAdvance.onEnded(Number(endedId), { stopAfterEpisode });
        });
        const unsubscribePlay = player.on("play", ({ state }) => {
            if (!playerStore.active || playerStore.isPlaying) {
                return;
            }

            playerStore.play(Math.floor(state.currentTime));
        });
        const unsubscribePause = player.on("pause", ({ state }) => {
            saveProgress(state, true);
            void listeningStore.flush();
            if (!playerStore.active || !playerStore.isPlaying) {
                return;
            }

            playerStore.pause(Math.floor(state.currentTime));
        });

        // Once the unfinished episodes are known (account or device), bring back the last one, paused where it was left
        const stopRestoring = when(
            () => listeningStore.loaded,
            () => restoreLastEpisode(listeningStore, playerStore, loadSource, readPlayerClosedAt())
        );

        return () => {
            stopRestoring();
            unsubscribe();
            clearInterval(sleepInterval);
            if (fadedFrom !== null) {
                player.setVolume(fadedFrom);
            }
            unsubscribeTime();
            unsubscribeEnded();
            autoAdvance.cancel();
            unsubscribePlay();
            unsubscribePause();
            mediaSession.destroy();
            player.destroy();
        };
    }, [player, loadSource]);

    const subscribe = useCallback((listener: () => void) => player.subscribe(() => listener()), [player]);

    const audioState = useMemo<AudioState>(
        () => ({
            subscribe,
            audio: playerState.currentSource ?? undefined,
            isPlaying: playerState.status === "playing",

            setAudio: loadSource,

            play() {
                void player.play().catch((error) => {
                    console.error("Failed to start audio playback", error);
                    playerStore.setPlayingState(false);
                });
            },

            pause() {
                player.pause();
            },

            seek(time) {
                if (time !== undefined) {
                    player.seek(time);
                }

                return player.getState().currentTime;
            },

            duration() {
                return player.getState().duration;
            },

            rate(speed) {
                if (speed !== undefined) {
                    player.setRate(speed);
                    return speed;
                }

                return player.getState().rate;
            },

            unload() {
                player.unload();
            },
        }),
        [player, loadSource, subscribe, playerState.currentSource, playerState.rate, playerState.status]
    );

    return <AudioContext.Provider value={audioState}>{children}</AudioContext.Provider>;
};
